import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  Edit3,
  RefreshCw,
  Search,
  Plus,
  X,
  Clock,
  FileSpreadsheet,
  AlertTriangle,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const INITIAL_DATA = {
  invoiceNo: "",
  dated: "",
  dueDate: "",
  vendor: {
    name: "",
    address: "",
    gstin: "",
    pan: "",
  },
  billTo: {
    name: "",
    address: "",
    gstin: "",
    pan: "",
    email: "",
    udyam: "",
    state: "",
  },
  lineItems: [],
  bank: {
    name: "",
    accountNo: "",
    branch: "",
    ifsc: "",
  },
  totals: {
    taxableValue: "",
    cgst: "",
    sgst: "",
    taxAmount: "",
    invoiceTotal: "",
  },
  notes: [],
  remarks: "",
};

const EMPTY_LINE_ITEM = { particulars: "", tripId: "", hsnSac: "", rate: "", taxable: "", cgst: "", sgst: "", total: "" };

function EditableField({ value, onChange, multiline = false, mono = false, placeholder = "—" }: any) {
  const [focused, setFocused] = useState(false);
  const baseStyle: React.CSSProperties = {
    fontFamily: mono ? "'DM Mono', monospace" : "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    background: focused ? "#f0fdf4" : "transparent",
    border: focused ? "1.5px solid #10b981" : "1.5px solid transparent",
    borderRadius: 6,
    padding: focused ? "3px 7px" : "3px 4px",
    width: "100%",
    resize: "none",
    outline: "none",
    lineHeight: 1.5,
    cursor: "text",
    transition: "all 0.15s ease",
    boxSizing: "border-box",
    display: "block",
  };
  if (multiline) {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        rows={2}
        style={baseStyle}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      style={baseStyle}
    />
  );
}

function Section({ title, icon, children, action }: any) {
  return (
    <div style={{ background: "white", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>{title}</h3>
        </div>
        {action}
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function FieldRow({ label, value, onChange, multiline, mono }: any) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
      <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>
        <EditableField value={value} onChange={onChange} multiline={multiline} mono={mono} placeholder={label} />
      </div>
    </div>
  );
}

function IconBtn({ onClick, title, children }: any) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 5px", borderRadius: 4, color: hov ? "#ef4444" : "#cbd5e1", fontSize: 13, lineHeight: 1, transition: "color 0.15s", fontFamily: "inherit" }}
    >
      {children}
    </button>
  );
}

/** Format currency like backend PDF */
function formatCurrency(amount: string | number | undefined) {
  if (amount === undefined || amount === null || amount === "") return "—";
  const num = Number(String(amount).replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return String(amount);
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Live invoice preview – matches PDF layout */
function InvoiceLivePreview({ data }: { data: any }) {
  const border = "1px solid #e5e7eb";
  const borderB = { borderBottom: "1px solid #e5e7eb" };
  const bgGray = { background: "#f3f4f6" };
  const textXs = { fontSize: 10, color: "#6b7280" };
  const textSm = { fontSize: 12 };
  const fontBold = { fontWeight: 700 };
  const invoiceLabel = { background: "#f3f4f6", color: "#111827", fontWeight: 700, padding: "10px 12px", width: "40%" };
  const invoiceValue = { padding: "10px 12px", color: "#0f172a", fontWeight: 700, fontSize: 12 };
  const invoiceValueSmall = { padding: "10px 12px", color: "#0f172a", fontWeight: 600, fontSize: 11 };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", background: "#fff", border, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", fontFamily: "Arial, sans-serif", fontSize: 12, color: "#1f2937" }}>
      {/* Header: Vendor (biller) details */}
      <div style={{ padding: "16px 20px", ...borderB }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{data.vendor?.name || "—"}</div>
        <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{data.vendor?.address || "—"}</div>
        <div style={textXs}>GSTIN/UIN: {data.vendor?.gstin || "—"}</div>
        <div style={textXs}>PAN: {data.vendor?.pan || "—"}</div>
      </div>

      {/* Invoice Details + Bill To */}
      <div style={{ border, ...borderB, borderBottomWidth: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr" }}>
          <div style={{ borderRight: "1px solid #e5e7eb" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr><td style={invoiceLabel}>Invoice No</td><td style={invoiceValueSmall}>{data.invoiceNo || "N/A"}</td></tr>
                <tr><td style={invoiceLabel}>Dated</td><td style={invoiceValue}>{data.dated || "N/A"}</td></tr>
                <tr><td style={invoiceLabel}>Due Date</td><td style={invoiceValue}>{data.dueDate || "N/A"}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div style={{ ...bgGray, padding: "12px", ...fontBold, ...borderB }}>Bill To</div>
            <div style={{ padding: 12 }}>
              <div style={{ ...textSm, ...fontBold }}>{data.billTo?.name || "—"}</div>
              <div style={{ ...textXs, whiteSpace: "pre-wrap" }}>{data.billTo?.address || "—"}</div>
              <div style={textXs}>GSTIN/UIN: {data.billTo?.gstin || "—"}</div>
              {data.billTo?.state && <div style={textXs}>State: {data.billTo.state}</div>}
              {data.billTo?.email && <div style={textXs}>Email: {data.billTo.email}</div>}
              {data.billTo?.udyam && <div style={textXs}>{data.billTo.udyam}</div>}
              <div style={textXs}>PAN: {data.billTo?.pan || "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div style={{ padding: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={bgGray}>
              <th style={{ padding: 10, border, ...fontBold }}>Particulars</th>
              <th style={{ padding: 10, border, ...fontBold }}>Trip ID</th>
              <th style={{ padding: 10, border, ...fontBold }}>HSN/SAC</th>
              <th style={{ padding: 10, border, ...fontBold }}>Rate</th>
              <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>Taxable</th>
              <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>CGST</th>
              <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>SGST</th>
              <th style={{ padding: 10, border, ...fontBold, textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.lineItems && data.lineItems.length > 0) ? data.lineItems.map((item: any, i: number) => (
              <tr key={i}>
                <td style={{ padding: 10, border, verticalAlign: "top" }}>
                  <div>{item.particulars || "Logistics Services"}</div>
                  {item.tripId && <div style={textXs}>Trip ID: {item.tripId}</div>}
                </td>
                <td style={{ padding: 10, border, verticalAlign: "top" }}>{item.tripId || "—"}</td>
                <td style={{ padding: 10, border, verticalAlign: "top" }}>{item.hsnSac || "—"}</td>
                <td style={{ padding: 10, border, verticalAlign: "top" }}>{item.rate || "—"}</td>
                <td style={{ padding: 10, border, textAlign: "right", verticalAlign: "top" }}>{formatCurrency(item.taxable)}</td>
                <td style={{ padding: 10, border, textAlign: "right", verticalAlign: "top" }}>{formatCurrency(item.cgst)}</td>
                <td style={{ padding: 10, border, textAlign: "right", verticalAlign: "top" }}>{formatCurrency(item.sgst)}</td>
                <td style={{ padding: 10, border, textAlign: "right", verticalAlign: "top", fontWeight: 600 }}>{formatCurrency(item.total)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#9ca3af", border }}>
                  No items listed.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals & Bank Details */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", ...borderB }}>
        <div style={{ borderRight: "1px solid #e5e7eb", padding: 16 }}>
          <div style={{ ...fontBold, marginBottom: 8 }}>Company Bank Details</div>
          <div style={textSm}>Bank Name: {data.bank?.name || "—"}</div>
          <div style={textSm}>A/C No.: {data.bank?.accountNo || "—"}</div>
          <div style={textSm}>Branch: {data.bank?.branch || "—"}</div>
          <div style={textSm}>IFSC Code: {data.bank?.ifsc || "—"}</div>
          {data.notes?.filter((n: string) => n?.trim()).length > 0 && (
            <>
              <div style={{ ...fontBold, marginTop: 12, marginBottom: 4 }}>Notes :</div>
              <div style={textXs}>{data.notes.filter((n: string) => n?.trim()).join("; ")}</div>
            </>
          )}
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>Total Taxable Value</span><span>{formatCurrency(data.totals?.taxableValue)}</span></div>
          {(data.totals?.cgst || data.totals?.cgst === 0) && <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>CGST</span><span>{formatCurrency(data.totals?.cgst)}</span></div>}
          {(data.totals?.sgst || data.totals?.sgst === 0) && <div style={{ ...textSm, display: "flex", justifyContent: "space-between" }}><span>SGST</span><span>{formatCurrency(data.totals?.sgst)}</span></div>}
          <div style={{ ...textSm, display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e5e7eb", paddingBottom: 8, marginTop: 8 }}><span>Tax Amount</span><span>{formatCurrency(data.totals?.taxAmount)}</span></div>
          <div style={{ fontSize: 16, ...fontBold, display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span>Invoice Total</span>
            <span style={{ color: "#15803d" }}>{formatCurrency(data.totals?.invoiceTotal)}</span>
          </div>
        </div>
      </div>

      {/* Notes & Remarks */}
      <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, borderTop: "1px solid #e5e7eb" }}>
        <div>
          <div style={{ ...fontBold, marginBottom: 8 }}>Notes :</div>
          <ul style={{ margin: 0, paddingLeft: 18, ...textXs }}>
            <li>Payment terms: As per agreed credit period.</li>
            <li>All disputes will fall under the jurisdiction of Mumbai courts.</li>
            <li>Please ensure payment is made within the due date to avoid any delays.</li>
          </ul>
        </div>
        <div>
          <div style={{ ...fontBold, marginBottom: 8 }}>Remarks :</div>
          <div style={textXs}>{data.remarks?.trim() || "Being GST in this invoice is to be paid by vendor."}</div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: "12px 16px", textAlign: "center", ...textXs, background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
        Subject to Mumbai jurisdiction, This is a Computer Generated Invoice
      </div>
    </div>
  );
}

function TCell({ value, onChange, mono, style = {} }: any) {
  return (
    <td style={{ padding: "5px 8px 5px 0", verticalAlign: "top", ...style }}>
      <div style={{ fontSize: 12, color: "#475569", fontFamily: mono ? "'DM Mono', monospace" : "inherit" }}>
        <EditableField value={value} onChange={onChange} mono={mono} multiline={!mono && (value?.length ?? 0) > 40} />
      </div>
    </td>
  );
}

const MONTH_CONFIG = [
  { short: "jan", full: "january", num: "01", val: 1 },
  { short: "feb", full: "february", num: "02", val: 2 },
  { short: "mar", full: "march", num: "03", val: 3 },
  { short: "apr", full: "april", num: "04", val: 4 },
  { short: "may", full: "may", num: "05", val: 5 },
  { short: "jun", full: "june", num: "06", val: 6 },
  { short: "jul", full: "july", num: "07", val: 7 },
  { short: "aug", full: "august", num: "08", val: 8 },
  { short: "sep", full: "september", num: "09", val: 9 },
  { short: "oct", full: "october", num: "10", val: 10 },
  { short: "nov", full: "november", num: "11", val: 11 },
  { short: "dec", full: "december", num: "12", val: 12 },
];

function checkDateMatch(inv: any, rawQuery: string): boolean {
  if (!rawQuery) return false;
  const q = String(rawQuery).trim().toLowerCase();
  if (!q) return false;

  const normalizeSep = (s: any) => String(s || "").toLowerCase().replace(/[-/. ]+/g, "-");

  const datedRaw = String(inv.dated || inv.invoiceDate || inv.date || "").toLowerCase();
  const dueRaw = String(inv.dueDate || "").toLowerCase();
  const selectedRaw = String(inv.selectedDate || "").toLowerCase();
  const createdRaw = inv.createdAt ? String(inv.createdAt).slice(0, 10).toLowerCase() : "";

  // 1. Direct substring on raw or normalized
  const allDateStrings = [datedRaw, dueRaw, selectedRaw, createdRaw].filter(Boolean);
  if (allDateStrings.some((s) => s.includes(q) || normalizeSep(s).includes(normalizeSep(q)))) {
    return true;
  }

  // 2. Full date checks (DD-MM-YYYY / DD/MM/YYYY / YYYY-MM-DD)
  const dmyMatch = q.match(/^(\d{1,2})[-/. ]+(\d{1,2})[-/. ]+(\d{4})$/);
  const ymdMatch = q.match(/^(\d{4})[-/. ]+(\d{1,2})[-/. ]+(\d{1,2})$/);

  let d = 0, m = 0, y = 0;
  if (dmyMatch) {
    d = parseInt(dmyMatch[1], 10);
    m = parseInt(dmyMatch[2], 10);
    y = parseInt(dmyMatch[3], 10);
  } else if (ymdMatch) {
    y = parseInt(ymdMatch[1], 10);
    m = parseInt(ymdMatch[2], 10);
    d = parseInt(ymdMatch[3], 10);
  }

  if (d && m && y && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
    const dPad = String(d).padStart(2, "0");
    const mPad = String(m).padStart(2, "0");
    const mInfo = MONTH_CONFIG[m - 1];

    const variants = [
      `${dPad}-${mPad}-${y}`,
      `${dPad}/${mPad}/${y}`,
      `${y}-${mPad}-${dPad}`,
      `${d}-${m}-${y}`,
      `${d}/${m}/${y}`,
      `${mInfo.short} ${d}, ${y}`,
      `${d} ${mInfo.short} ${y}`,
      `${mInfo.full} ${d}, ${y}`,
      `${d} ${mInfo.full} ${y}`,
      `${dPad}-${mInfo.short}-${y}`,
      `${dPad}/${mInfo.short}/${y}`,
    ];

    for (const v of variants) {
      const normV = normalizeSep(v);
      for (const t of allDateStrings) {
        if (t.includes(v.toLowerCase()) || normalizeSep(t).includes(normV)) return true;
      }
    }
  }

  // 3. Partial date (DD-MM or DD/MM)
  const dmMatch = q.match(/^(\d{1,2})[-/. ]+(\d{1,2})$/);
  if (dmMatch) {
    const partD = parseInt(dmMatch[1], 10);
    const partM = parseInt(dmMatch[2], 10);
    if (partM >= 1 && partM <= 12 && partD >= 1 && partD <= 31) {
      const dPad = String(partD).padStart(2, "0");
      const mPad = String(partM).padStart(2, "0");
      const mInfo = MONTH_CONFIG[partM - 1];
      const variants = [
        `${dPad}-${mPad}`,
        `${dPad}/${mPad}`,
        `${partD}-${partM}`,
        `${partD}/${partM}`,
        `${mInfo.short} ${partD}`,
        `${partD} ${mInfo.short}`,
      ];
      for (const v of variants) {
        const normV = normalizeSep(v);
        for (const t of allDateStrings) {
          if (t.includes(v.toLowerCase()) || normalizeSep(t).includes(normV)) return true;
        }
      }
    }
  }

  // 4. Month name matching (e.g. "Aug", "August", "Sep", "September")
  for (const mInfo of MONTH_CONFIG) {
    if (q.startsWith(mInfo.short) || q.startsWith(mInfo.full)) {
      for (const t of allDateStrings) {
        if (
          t.includes(mInfo.short) ||
          t.includes(`-${mInfo.num}-`) ||
          t.includes(`/${mInfo.num}/`) ||
          t.includes(`-${mInfo.num}`) ||
          t.includes(`/${mInfo.num}`)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

const Invoice: React.FC = () => {
  const { user } = useAuth();
  const vendorEmail = (user?.email || (() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return ud.email || ud.name || '';
    } catch { return ''; }
  })()).trim();

  // Extraction & Editor States
  const [data, setData] = useState<any>(INITIAL_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dbRecordId, setDbRecordId] = useState<string | null>(null);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [uploadedS3Url, setUploadedS3Url] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRejectionReason, setEditingRejectionReason] = useState<string | null>(null);
  const [viewReasonModal, setViewReasonModal] = useState<{ isOpen: boolean; invoiceNo: string; reason: string; invoice: any } | null>(null);
  const [duplicateModal, setDuplicateModal] = useState<{ isOpen: boolean; message: string } | null>(null);
  const [hasEdited, setHasEdited] = useState(false);
  const [isDuplicateLoaded, setIsDuplicateLoaded] = useState(false);

  // Uploaded Invoices List States
  const [uploadedList, setUploadedList] = useState<any[]>([]);
  const uploadedListRef = useRef<any[]>([]);
  useEffect(() => {
    uploadedListRef.current = uploadedList;
  }, [uploadedList]);
  const [loadingList, setLoadingList] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"ALL" | "MATCHED" | "PENDING">("ALL");
  const [listPage, setListPage] = useState<number>(1);
  const listLimit = 10;

  // Check if current invoice number is an active duplicate for this vendor
  const isCurrentDuplicate = useMemo(() => {
    if (!data.invoiceNo || uploadedList.length === 0) return false;
    const cleanNo = String(data.invoiceNo).trim().toLowerCase();
    return uploadedList.some((inv) => {
      const existingNo = String(inv.invoiceNo || "").trim().toLowerCase();
      const isRejected = [
        "REJECTED_BY_OPS",
        "REJECTED_BY_FINOPS",
        "REJECTED",
      ].includes(String(inv.status || "").toUpperCase());
      return existingNo === cleanNo && String(inv._id) !== String(dbRecordId) && !isRejected;
    });
  }, [data.invoiceNo, uploadedList, dbRecordId]);

  // For duplicate uploads or editing existing rejected invoices, user must edit first.
  // For brand new invoice uploads (non-duplicate), submit is enabled immediately.
  const requiresEditBeforeSubmit = Boolean(isCurrentDuplicate || isDuplicateLoaded || editingRejectionReason || dbRecordId);

  const isSubmitDisabled = Boolean(
    submitting ||
    loading ||
    !data.invoiceNo ||
    isCurrentDuplicate ||
    (requiresEditBeforeSubmit && !hasEdited)
  );

  // Revoke object URL on unmount or when PDF changes
  useEffect(() => {
    return () => {
      if (uploadedPdfUrl) URL.revokeObjectURL(uploadedPdfUrl);
    };
  }, [uploadedPdfUrl]);

  // Fetch only invoices uploaded by the current vendor
  const fetchUploadedInvoices = useCallback(async () => {
    setLoadingList(true);
    try {
      let rawInvoices: any[] = [];

      const currentEmail = (
        user?.email ||
        (() => {
          try {
            const ud = JSON.parse(localStorage.getItem('userData') || '{}');
            return ud.email || '';
          } catch { return ''; }
        })()
      ).trim().toLowerCase();

      const currentName = (
        user?.name ||
        (() => {
          try {
            const ud = JSON.parse(localStorage.getItem('userData') || '{}');
            return ud.name || '';
          } catch { return ''; }
        })()
      ).trim().toLowerCase();

      const currentId = (
        user?._id ||
        (() => {
          try {
            const ud = JSON.parse(localStorage.getItem('userData') || '{}');
            return ud._id || '';
          } catch { return ''; }
        })()
      ).trim();

      const currentGst = (
        user?.gstNumber ||
        (() => {
          try {
            const ud = JSON.parse(localStorage.getItem('userData') || '{}');
            return ud.gstNumber || '';
          } catch { return ''; }
        })()
      ).trim().toLowerCase();

      // 1. Try local FinOps details endpoint first with vendor filter
      try {
        const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/vendors').replace(/\/api\/vendors\/?$/, '/api');
        const token = localStorage.getItem('authToken');
        const queryParams = currentEmail ? `?uploadedBy=${encodeURIComponent(currentEmail)}&limit=100` : '?limit=100';
        const resLocal = await fetch(`${apiBase}/finops/vendor-invoice-details${queryParams}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const jsonLocal = await resLocal.json();
        if (jsonLocal?.success && Array.isArray(jsonLocal.data)) {
          rawInvoices = jsonLocal.data;
        }
      } catch (e) { }

      // 2. Also try Vercel Invoices API and merge
      try {
        const res = await fetch("https://ai-backend.justdeliveries.ai/api/invoices");
        const json = await res.json();
        if (json?.success && Array.isArray(json.invoices)) {
          // Merge unique invoices
          const existingIds = new Set(rawInvoices.map(i => String(i._id || i.invoiceNo)));
          json.invoices.forEach((vInv: any) => {
            if (!existingIds.has(String(vInv._id || vInv.invoiceNo))) {
              rawInvoices.push(vInv);
            }
          });
        }
      } catch (e) {
        console.warn("Could not fetch from vercel invoices api:", e);
      }

      // 3. STRICT FILTER: Only show what this specific vendor has uploaded
      const myOnlyInvoices = rawInvoices.filter((inv) => {
        const uploader = String(inv.uploadedBy || "").trim().toLowerCase();
        const invVendorName = String(inv.vendor?.name || "").trim().toLowerCase();
        const invVendorId = String(inv.selectedVendorId || inv.vendorId || "").trim();
        const invGst = String(inv.vendor?.gstin || "").trim().toLowerCase();

        // Exact match by uploader email
        if (currentEmail && uploader && uploader === currentEmail) return true;

        // Match by vendor ObjectId
        if (currentId && invVendorId && invVendorId === currentId) return true;

        // Match by vendor GSTIN
        if (currentGst && invGst && invGst === currentGst) return true;

        // Match by exact/close vendor business name
        if (currentName && invVendorName) {
          if (invVendorName === currentName || invVendorName.includes(currentName) || currentName.includes(invVendorName)) {
            return true;
          }
        }

        // Match if created in the current active session
        if (dbRecordId && inv._id === dbRecordId) return true;

        return false;
      });

      setUploadedList(myOnlyInvoices);
    } catch (err) {
      console.error("Error fetching uploaded invoices:", err);
      setUploadedList([]);
    } finally {
      setLoadingList(false);
    }
  }, [user, dbRecordId]);

  useEffect(() => {
    fetchUploadedInvoices();
  }, [fetchUploadedInvoices]);

  const update = (path: string, value: any) => {
    setHasEdited(true);
    setData((prev: any) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });

  };

  const updateLineItem = (idx: number, field: string, value: any) => {
    setHasEdited(true);
    setData((prev: any) => ({ ...prev, lineItems: prev.lineItems.map((it: any, i: number) => i === idx ? { ...it, [field]: value } : it) }));

  };

  const addLineItem = () => {
    setHasEdited(true); setData((prev: any) => ({ ...prev, lineItems: [...prev.lineItems, { ...EMPTY_LINE_ITEM }] }));
  };
  const removeLineItem = (idx: number) => {
    setHasEdited(true); setData((prev: any) => ({ ...prev, lineItems: prev.lineItems.filter((_: any, i: number) => i !== idx) }));
  };

  const updateNote = (idx: number, value: any) => {
    setHasEdited(true);
    setData((prev: any) => ({ ...prev, notes: prev.notes.map((n: any, i: number) => i === idx ? value : n) }));

  };
  const addNote = () => {
    setHasEdited(true); setData((prev: any) => ({ ...prev, notes: [...prev.notes, ""] }));
  };
  const removeNote = (idx: number) => {
    setHasEdited(true); setData((prev: any) => ({ ...prev, notes: prev.notes.filter((_: any, i: number) => i !== idx) }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const uploaderEmail = (
        vendorEmail ||
        user?.email ||
        (() => {
          try {
            const ud = JSON.parse(localStorage.getItem('userData') || '{}');
            return ud.email || ud.name || '';
          } catch { return ''; }
        })() ||
        'vendor'
      ).trim();

      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/vendors').replace(/\/api\/vendors\/?$/, '/api');
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const invoicePayload = {
        ...data,
        fileName: fileName || "Invoice.pdf",
        fileUrl: uploadedS3Url || "",
        uploadedBy: uploaderEmail,
        uploadedFrom: "Vendor Portal",
        selectedVendorId: user?._id || undefined,
        status: "PENDING_BY_OPS",
      };

      // Direct Save to FinOps backend API (creates single unified invoice record)
      const saveEndpoint = dbRecordId && !dbRecordId.startsWith('temp_')
        ? `${apiBase}/finops/vendor-invoice-details/${dbRecordId}`
        : `${apiBase}/finops/vendor-invoice-details`;

      const saveMethod = dbRecordId && !dbRecordId.startsWith('temp_') ? "PUT" : "POST";

      const localRes = await fetch(saveEndpoint, {
        method: saveMethod,
        headers,
        body: JSON.stringify(invoicePayload),
      });

      const localJson = await localRes.json();
      if (!localRes.ok || !localJson?.success) {
        const errorMsg = localJson?.message || "Failed to save invoice";
        throw new Error(errorMsg);
      }
      if (localJson?.success && localJson?.data?._id) {
        setDbRecordId(localJson.data._id);
      }




      await fetchUploadedInvoices();
    } catch (err: any) {
      setError("Failed to save to database: " + err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (uploadedPdfUrl) URL.revokeObjectURL(uploadedPdfUrl);
    setUploadedPdfUrl(null);
    setUploadedS3Url(null);
    setFileName(null);
    setData(INITIAL_DATA);
    setDbRecordId(null);
    setHasEdited(false);
    setIsDuplicateLoaded(false);


    setSubmittedSuccess(false);
    setError(null);
    setEditingRejectionReason(null);
    setIsEditorOpen(false);
  };

  const handleSubmitInvoice = async () => {
    if (!data.invoiceNo && !fileName && !uploadedPdfUrl) {
      setError("Please upload an invoice before submitting.");
      return;
    }

    // Client-side duplicate check for this vendor
    if (data.invoiceNo && uploadedList.length > 0) {
      const cleanNo = String(data.invoiceNo).trim().toLowerCase();
      const duplicateInvoice = uploadedList.find((inv) => {
        const existingNo = String(inv.invoiceNo || "").trim().toLowerCase();
        const isRejected = [
          "REJECTED_BY_OPS",
          "REJECTED_BY_FINOPS",
          "REJECTED",
        ].includes(String(inv.status || "").toUpperCase());
        return existingNo === cleanNo && String(inv._id) !== String(dbRecordId) && !isRejected;
      });

      if (duplicateInvoice) {
        const dupMsg = `Invoice #${data.invoiceNo} has already been uploaded and is under review. Duplicate uploads are not allowed.`;
        setError(dupMsg);
        setDuplicateModal({ isOpen: true, message: dupMsg });
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      await handleSave();
      setSubmittedSuccess(true);
      setHasEdited(false);
      setIsDuplicateLoaded(false);
      setIsEditorOpen(false);
      setData(INITIAL_DATA);
      setFileName(null);
      setUploadedPdfUrl(null);
      setDbRecordId(null);
      setEditingRejectionReason(null);
    } catch (err: any) {
      const errMsg = err.message || "Failed to submit invoice";
      setError(errMsg);
      if (errMsg.includes("already been uploaded")) {
        setDuplicateModal({ isOpen: true, message: errMsg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const extractFromPDF = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setIsEditorOpen(true);
    try {
      const base64: string = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res((reader.result as string).split(",")[1]);
        reader.onerror = () => rej(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      const result = await apiService.extractInvoice(base64, file.name, file.type);
      const { fileUrl, ...parsed } = result as any;

      if (fileUrl) {
        setUploadedS3Url(fileUrl);
      }

      setFileName(file.name);
      setHasEdited(false);

      // Check if duplicate on OCR extraction
      if (parsed.invoiceNo && uploadedListRef.current && uploadedListRef.current.length > 0) {
        const cleanNo = String(parsed.invoiceNo).trim().toLowerCase();
        const dup = uploadedListRef.current.find((inv: any) => {
          const existingNo = String(inv.invoiceNo || "").trim().toLowerCase();
          const isRejected = [
            "REJECTED_BY_OPS",
            "REJECTED_BY_FINOPS",
            "REJECTED",
          ].includes(String(inv.status || "").toUpperCase());
          return existingNo === cleanNo && !isRejected;
        });

        if (dup) {
          const dupMsg = `Invoice #${parsed.invoiceNo} has already been uploaded and is under review. Duplicate uploads are not allowed.`;
          setError(dupMsg);
          setIsDuplicateLoaded(true);
          setDuplicateModal({ isOpen: true, message: dupMsg });
        } else {
          setIsDuplicateLoaded(false);
        }
      }
      setDbRecordId(parsed.id || null);

      setData({
        invoiceNo: parsed.invoiceNo || "",
        dated: parsed.dated || "",
        dueDate: parsed.dueDate || "",
        vendor: {
          name: parsed.vendor?.name || "",
          address: parsed.vendor?.address || "",
          gstin: parsed.vendor?.gstin || "",
          pan: parsed.vendor?.pan || "",
        },
        billTo: {
          name: parsed.billTo?.name || "",
          address: parsed.billTo?.address || "",
          gstin: parsed.billTo?.gstin || "",
          pan: parsed.billTo?.pan || "",
          email: parsed.billTo?.email || "",
          udyam: parsed.billTo?.udyam || "",
          state: parsed.billTo?.state || "",
        },
        lineItems: Array.isArray(parsed.lineItems) ? parsed.lineItems : [],
        bank: {
          name: parsed.bank?.name || "",
          accountNo: parsed.bank?.accountNo || "",
          branch: parsed.bank?.branch || "",
          ifsc: parsed.bank?.ifsc || "",
        },
        totals: {
          taxableValue: parsed.totals?.taxableValue || "",
          cgst: parsed.totals?.cgst || "",
          sgst: parsed.totals?.sgst || "",
          taxAmount: parsed.totals?.taxAmount || "",
          invoiceTotal: parsed.totals?.invoiceTotal || "",
        },
        notes: Array.isArray(parsed.notes) ? parsed.notes : [],
        remarks: parsed.remarks || "",
      });


      setIsEditorOpen(true);
    } catch (err: any) {
      setError("AI Extraction failed: " + (err?.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const validExtensions = /\.(pdf|jpg|jpeg|png|webp|gif|bmp|tiff|tif|heic)$/i;
    const isPdfOrImage = file.type === "application/pdf" || file.type.startsWith("image/") || validExtensions.test(file.name);
    if (!isPdfOrImage) {
      setError("Please upload a valid PDF or image file (JPEG, PNG, WEBP, GIF, BMP, TIFF, etc.).");
      return;
    }
    if (uploadedPdfUrl) URL.revokeObjectURL(uploadedPdfUrl);
    setUploadedPdfUrl(URL.createObjectURL(file));
    setSubmittedSuccess(false);
    extractFromPDF(file);
  };

  // Load a previously uploaded invoice into the editor & preview
  const handleSelectInvoiceToEdit = (inv: any) => {
    setHasEdited(false);
    setIsDuplicateLoaded(false);
    setData({
      invoiceNo: inv.invoiceNo || "",
      dated: inv.dated || "",
      dueDate: inv.dueDate || "",
      vendor: inv.vendor || INITIAL_DATA.vendor,
      billTo: inv.billTo || INITIAL_DATA.billTo,
      lineItems: Array.isArray(inv.lineItems) ? inv.lineItems : [],
      bank: inv.bank || INITIAL_DATA.bank,
      totals: inv.totals || INITIAL_DATA.totals,
      notes: Array.isArray(inv.notes) ? inv.notes : [],
      remarks: inv.remarks || "",
    });
    setFileName(inv.fileName || "Invoice.pdf");
    setUploadedS3Url(inv.fileUrl || null);
    setDbRecordId(inv._id || null);
    setEditingRejectionReason(inv.rejectionReason || null);

    setIsEditorOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Filtered Invoices List
  const filteredList = useMemo(() => {
    let list = uploadedList;
    if (activeTab === "MATCHED") {
      list = list.filter((inv) => inv.systemMatched === true);
    } else if (activeTab === "PENDING") {
      list = list.filter((inv) => !inv.systemMatched);
    }

    if (listSearch.trim()) {
      const q = listSearch.toLowerCase().trim();
      list = list.filter((inv) =>
        String(inv.invoiceNo || "").toLowerCase().includes(q) ||
        String(inv.vendor?.name || "").toLowerCase().includes(q) ||
        String(inv.billTo?.name || "").toLowerCase().includes(q) ||
        String(inv.totals?.invoiceTotal || "").toLowerCase().includes(q) ||
        String(inv.fileName || "").toLowerCase().includes(q) ||
        String(inv.status || "").toLowerCase().includes(q) ||
        String(inv.dated || "").toLowerCase().includes(q) ||
        String(inv.invoiceDate || "").toLowerCase().includes(q) ||
        String(inv.selectedDate || "").toLowerCase().includes(q) ||
        String(inv.dueDate || "").toLowerCase().includes(q) ||
        checkDateMatch(inv, q)
      );
    }
    return list;
  }, [uploadedList, activeTab, listSearch]);

  // Reset to page 1 on search or filter tab change
  useEffect(() => {
    setListPage(1);
  }, [listSearch, activeTab]);

  const totalListPages = Math.ceil(filteredList.length / listLimit) || 1;
  const paginatedInvoices = useMemo(() => {
    const start = (listPage - 1) * listLimit;
    return filteredList.slice(start, start + listLimit);
  }, [filteredList, listPage, listLimit]);

  const totalUploadedValue = useMemo(() => {
    return uploadedList.reduce((acc, inv) => {
      const raw = inv.totals?.taxableValue || inv.taxableValue || inv.totals?.invoiceTotal || 0;
      const num = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
      return acc + (isNaN(num) ? 0 : num);
    }, 0);
  }, [uploadedList]);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: "#f8fafc", minHeight: "100vh", padding: "24px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input, textarea { font-family: inherit; }
        input::placeholder, textarea::placeholder { color: #cbd5e1; font-style: italic; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: fadeIn 0.35s ease forwards; }
        .row-actions { opacity: 0; transition: opacity 0.15s; }
        tr:hover .row-actions, .note-row:hover .row-actions { opacity: 1; }
        input:hover, textarea:hover { background: #f8fafc !important; border-color: #e2e8f0 !important; }
        @media (max-width: 1024px) {
          .invoice-with-preview { grid-template-columns: 1fr !important; }
          .invoice-with-preview .pdf-sticky { position: static !important; }
        }
      `}</style>

      {/* TOP HEADER BAR WITH PROMINENT UPLOAD BUTTON */}
      <div style={{ background: "white", borderRadius: 18, border: "1px solid #e2e8f0", padding: "18px 24px", marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)" }}>
            <FileText size={22} />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>Upload & Manage Invoices</h1>
            <p style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              Upload invoices for automated AI field extraction, verify details, and track all submitted records.
            </p>
          </div>
        </div>

        {/* Top Actions: Prominent Upload Button & Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={fetchUploadedInvoices}
            disabled={loadingList}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#475569", padding: "9px 14px", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
            title="Refresh uploaded invoices list"
          >
            <RefreshCw size={14} className={loadingList ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={() => {
              const input = document.getElementById("pdf-upload-top");
              if (input) input.click();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
              transition: "all 0.2s ease",
            }}
          >
            <Upload size={16} />
            <span>Upload Invoice</span>
          </button>
          <input
            id="pdf-upload-top"
            type="file"
            accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.tif,.heic"
            style={{ display: "none" }}
            onChange={(e) => e.target.files && handleFile(e.target.files[0])}
          />
        </div>
      </div>

      {/* QUICK STATS CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
        <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ecfdf5", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FileSpreadsheet size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Uploaded</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>{uploadedList.length} invoices</div>
          </div>
        </div>

        <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircle2 size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Matched Invoices</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1e40af", marginTop: 2 }}>
              {uploadedList.filter(i => i.systemMatched).length} invoices
            </div>
          </div>
        </div>

        <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fef3c7", color: "#d97706", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Clock size={20} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Pending Review</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#b45309", marginTop: 2 }}>
              {uploadedList.filter(i => !i.systemMatched).length} invoices
            </div>
          </div>
        </div>

        <div style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#f0fdf4", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, fontWeight: 800 }}>₹</span>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Uploaded Value</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#065f46", marginTop: 2, fontFamily: "'DM Mono', monospace" }}>
              ₹{totalUploadedValue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      {submittedSuccess && (
        <div style={{ background: "#ecfdf5", border: "1.5px solid #a7f3d0", borderRadius: 14, padding: "14px 20px", marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#10b981", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>✓</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#065f46" }}>Invoice Successfully Submitted & Saved!</div>
              <div style={{ fontSize: 12, color: "#047857" }}>Invoice #{data.invoiceNo || fileName || "New"} has been sent to Control Tower for review.</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSubmittedSuccess(false)}
            style={{ background: "#059669", color: "white", border: "none", padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* AI Extraction Loading Banner */}
      {loading && (
        <div style={{ background: "#ecfdf5", border: "1.5px solid #a7f3d0", borderRadius: 14, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ width: 22, height: 22, border: "2.5px solid #a7f3d0", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ fontSize: 13, color: "#065f46", fontWeight: 700 }}>Extracting invoice fields with AI OCR... Please wait.</span>
        </div>
      )}

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "12px 16px", marginBottom: 18, color: "#ef4444", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* INVOICE DRAFT IN-PROGRESS RESUME BANNER (Only shown for newly uploaded UN-SUBMITTED drafts when editor is hidden) */}
      {!isEditorOpen && !dbRecordId && (fileName || uploadedPdfUrl || data.invoiceNo || (data.lineItems && data.lineItems.length > 0)) && (
        <div
          style={{
            background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
            border: "1.5px solid #86efac",
            borderRadius: 16,
            padding: "16px 22px",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 4px 16px rgba(16, 185, 129, 0.12)",
            flexWrap: "wrap",
            gap: 16,
          }}
          className="fade-in"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "#10b981",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(16, 185, 129, 0.3)",
              }}
            >
              <FileText size={22} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#065f46" }}>
                  Active Invoice in Progress: <span style={{ fontFamily: "'DM Mono', monospace" }}>{data.invoiceNo || fileName || "New Invoice"}</span>
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    background: "#bbf7d0",
                    color: "#166534",
                    padding: "2px 8px",
                    borderRadius: 6,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Ready to Review & Submit
                </span>
              </div>
              <p style={{ fontSize: 12, color: "#047857", marginTop: 3 }}>
                File: <strong>{fileName || "Invoice.pdf"}</strong>
                {data.totals?.taxableValue && (
                  <span style={{ marginLeft: 12 }}>
                    Taxable: <strong>₹{data.totals.taxableValue}</strong>
                    {data.totals.invoiceTotal && (
                      <span> (Total: ₹{data.totals.invoiceTotal})</span>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => setIsEditorOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#059669",
                color: "white",
                border: "none",
                padding: "9px 18px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(5, 150, 105, 0.3)",
              }}
            >
              <Edit3 size={14} />
              <span>Show / Resume Editor</span>
            </button>

            <button
              type="button"
              onClick={handleSubmitInvoice}
              disabled={submitting}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#0f172a",
                color: "white",
                border: "none",
                padding: "9px 18px",
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <CheckCircle2 size={14} />
              <span>{submitting ? "Submitting..." : "Submit Invoice"}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (window.confirm("Are you sure you want to discard this unsubmitted invoice draft?")) {
                  handleCancel();
                }
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "transparent",
                color: "#b91c1c",
                border: "1px solid #fca5a5",
                padding: "8px 12px",
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
              title="Discard draft"
            >
              <Trash2 size={13} />
              <span>Discard</span>
            </button>
          </div>
        </div>
      )}

      {/* AI EXTRACTION EDITOR & LIVE PREVIEW (SHOWN WHEN ACTIVE OR AN INVOICE IS SELECTED) */}
      {isEditorOpen && (
        <div style={{ marginBottom: 26 }} className="fade-in">
          {editingRejectionReason && (
            <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 12, padding: "12px 16px", marginBottom: 14, color: "#991b1b", fontSize: 12, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <XCircle size={16} color="#dc2626" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong style={{ fontWeight: 800, textTransform: "uppercase", fontSize: 11, letterSpacing: "0.05em", color: "#b91c1c", display: "block" }}>Invoice Rejected by Ops</strong>
                <div style={{ marginTop: 2, color: "#7f1d1d", fontWeight: 500 }}>
                  <strong>Rejection Reason:</strong> {editingRejectionReason}
                </div>
              </div>
            </div>
          )}
          <div style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: 14, padding: "12px 18px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Edit3 size={16} color="#059669" />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                Invoice Editor: <strong style={{ fontFamily: "'DM Mono', monospace" }}>{data.invoiceNo || fileName || "New Invoice"}</strong>
              </span>
              <span style={{ fontSize: 11, background: "#e2e8f0", color: "#475569", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>
                All fields editable
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (dbRecordId) {
                  handleCancel();
                } else {
                  setIsEditorOpen(false);
                }
              }}
              style={{ background: "white", border: "1px solid #cbd5e1", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              <X size={13} />
              <span>Hide Editor</span>
            </button>
          </div>

          <div className="invoice-with-preview" style={{ display: "grid", gridTemplateColumns: "1fr minmax(340px, 440px)", gap: 20, alignItems: "start" }}>
            <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Row 1: Invoice Details, Vendor, Bill To */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <Section title="Invoice Details" icon="🧾">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <FieldRow label="Invoice No" value={data.invoiceNo} onChange={(v: any) => update("invoiceNo", v)} />
                    <FieldRow label="Date" value={data.dated} onChange={(v: any) => update("dated", v)} />
                    <FieldRow label="Due Date" value={data.dueDate} onChange={(v: any) => update("dueDate", v)} />
                  </div>
                </Section>

                <Section title="Vendor" icon="🏢">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <FieldRow label="Name" value={data.vendor.name} onChange={(v: any) => update("vendor.name", v)} />
                    <FieldRow label="Address" value={data.vendor.address} onChange={(v: any) => update("vendor.address", v)} multiline />
                    <FieldRow label="GSTIN" value={data.vendor.gstin} onChange={(v: any) => update("vendor.gstin", v)} mono />
                    <FieldRow label="PAN" value={data.vendor.pan} onChange={(v: any) => update("vendor.pan", v)} mono />
                  </div>
                </Section>

                <Section title="Bill To" icon="📬">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <FieldRow label="Company" value={data.billTo.name} onChange={(v: any) => update("billTo.name", v)} />
                    <FieldRow label="Address" value={data.billTo.address} onChange={(v: any) => update("billTo.address", v)} multiline />
                    <FieldRow label="GSTIN" value={data.billTo.gstin} onChange={(v: any) => update("billTo.gstin", v)} mono />
                    <FieldRow label="State" value={data.billTo.state} onChange={(v: any) => update("billTo.state", v)} />
                  </div>
                </Section>
              </div>

              {/* Row 2: Line Items Table */}
              <Section
                title={`Line Items (${data.lineItems.length})`}
                icon="📦"
                action={
                  <button
                    onClick={addLineItem}
                    style={{ background: "#f0fdf4", border: "1px solid #86efac", color: "#16a34a", padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <Plus size={12} /> Add Item
                  </button>
                }
              >
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: "1.5px solid #f1f5f9", textAlign: "left" }}>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Particulars</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 70 }}>Trip ID</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 65 }}>HSN/SAC</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 65 }}>Rate</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 80 }}>Taxable</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 65 }}>CGST</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 65 }}>SGST</th>
                        <th style={{ padding: "0 8px 8px 0", fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", width: 85 }}>Total</th>
                        <th style={{ width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lineItems.map((item: any, i: number) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                          <TCell value={item.particulars} onChange={(v: any) => updateLineItem(i, "particulars", v)} />
                          <TCell value={item.tripId} onChange={(v: any) => updateLineItem(i, "tripId", v)} mono />
                          <TCell value={item.hsnSac} onChange={(v: any) => updateLineItem(i, "hsnSac", v)} mono />
                          <TCell value={item.rate} onChange={(v: any) => updateLineItem(i, "rate", v)} mono />
                          <TCell value={item.taxable} onChange={(v: any) => updateLineItem(i, "taxable", v)} mono />
                          <TCell value={item.cgst} onChange={(v: any) => updateLineItem(i, "cgst", v)} mono />
                          <TCell value={item.sgst} onChange={(v: any) => updateLineItem(i, "sgst", v)} mono />
                          <TCell value={item.total} onChange={(v: any) => updateLineItem(i, "total", v)} mono style={{ fontWeight: 600, color: "#0f172a" }} />
                          <td style={{ padding: "5px 0", verticalAlign: "top", textAlign: "right" }}>
                            <span className="row-actions">
                              <IconBtn onClick={() => removeLineItem(i)} title="Remove item">✕</IconBtn>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              {/* Row 3: Bank, Totals & Notes */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <Section title="Bank Details" icon="🏦">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <FieldRow label="Bank Name" value={data.bank.name} onChange={(v: any) => update("bank.name", v)} />
                    <FieldRow label="Account No" value={data.bank.accountNo} onChange={(v: any) => update("bank.accountNo", v)} mono />
                    <FieldRow label="Branch" value={data.bank.branch} onChange={(v: any) => update("bank.branch", v)} />
                    <FieldRow label="IFSC Code" value={data.bank.ifsc} onChange={(v: any) => update("bank.ifsc", v)} mono />
                  </div>
                </Section>

                <Section title="Tax & Totals" icon="💰">
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <FieldRow label="Taxable Value" value={data.totals.taxableValue} onChange={(v: any) => update("totals.taxableValue", v)} mono />
                    <FieldRow label="CGST" value={data.totals.cgst} onChange={(v: any) => update("totals.cgst", v)} mono />
                    <FieldRow label="SGST" value={data.totals.sgst} onChange={(v: any) => update("totals.sgst", v)} mono />
                    <FieldRow label="Tax Amount" value={data.totals.taxAmount} onChange={(v: any) => update("totals.taxAmount", v)} mono />
                    <FieldRow label="Invoice Total" value={data.totals.invoiceTotal} onChange={(v: any) => update("totals.invoiceTotal", v)} mono />
                  </div>
                </Section>

                <Section
                  title="Notes & Remarks"
                  icon="📝"
                  action={
                    <button
                      onClick={addNote}
                      style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b", padding: "2px 7px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                    >
                      + Note
                    </button>
                  }
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {data.notes.map((note: any, i: number) => (
                      <div key={i} className="note-row" style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                        <span style={{ color: "#cbd5e1", marginTop: 5, flexShrink: 0, fontSize: 12 }}>•</span>
                        <div style={{ flex: 1, fontSize: 12, color: "#475569" }}>
                          <EditableField value={note} onChange={(v: any) => updateNote(i, v)} multiline placeholder="Note text..." />
                        </div>
                        <span className="row-actions" style={{ flexShrink: 0, marginTop: 3 }}>
                          <IconBtn onClick={() => removeNote(i)} title="Remove note">✕</IconBtn>
                        </span>
                      </div>
                    ))}
                    <div style={{ marginTop: 8, borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Remarks</div>
                      <div style={{ fontSize: 12, color: "#475569" }}>
                        <EditableField value={data.remarks} onChange={(v: any) => update("remarks", v)} placeholder="Remarks..." />
                      </div>
                    </div>
                  </div>
                </Section>
              </div>

              {/* Editor Footer Action Bar - Clean White Theme */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: 16,
                  padding: "16px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 16,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05)",
                }}
              >
                <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
                  {[
                    { label: "Invoice", value: data.invoiceNo, mono: true, color: "#0f172a" },
                    { label: "Due", value: data.dueDate, mono: false, color: "#d97706" },
                    { label: "Line Items", value: data.lineItems.length, mono: false, color: "#0f172a" },
                  ].map(({ label, value, mono, color }) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                      <div style={{ fontSize: 15, color, fontWeight: 700, fontFamily: mono ? "'DM Mono', monospace" : "inherit" }}>{value || "—"}</div>
                    </div>
                  ))}
                  {isCurrentDuplicate ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: 11, fontWeight: 700 }}>
                      <AlertCircle size={13} />
                      Duplicate under review - Edit invoice to enable submit
                    </div>
                  ) : (requiresEditBeforeSubmit && !hasEdited) ? (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b", fontSize: 11, fontWeight: 600 }}>
                      Edit details to enable submit
                    </div>
                  ) : null}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #cbd5e1",
                      color: "#475569",
                      padding: "10px 18px",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    Cancel / Close
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitInvoice}
                    disabled={isSubmitDisabled}
                    title={
                      isCurrentDuplicate
                        ? `Invoice #${data.invoiceNo} is a duplicate under review. Please edit invoice number to submit.`
                        : (requiresEditBeforeSubmit && !hasEdited)
                          ? "Please edit invoice details to enable submit"
                          : "Submit Invoice"
                    }
                    style={{
                      background: isSubmitDisabled ? "#f1f5f9" : "#10b981",
                      border: isSubmitDisabled ? "1px solid #e2e8f0" : "none",
                      color: isSubmitDisabled ? "#94a3b8" : "white",
                      padding: "10px 24px",
                      borderRadius: 10,
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                      opacity: isSubmitDisabled ? 0.7 : 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: isSubmitDisabled ? "none" : "0 4px 14px rgba(16, 185, 129, 0.35)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {submitting ? (
                      <>
                        <div style={{ width: 14, height: 14, border: "2px solid #94a3b8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} />
                        <span>Submit Invoice</span>
                      </>
                    )}
                  </button>
                  <div style={{ textAlign: "right", paddingLeft: 4 }}>
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Total Amount</div>
                    <div style={{ fontSize: 22, color: "#059669", fontWeight: 800, fontFamily: "'DM Mono', monospace" }}>{data.totals.invoiceTotal || "₹0.00"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Invoice Preview Column */}
            <div className="pdf-sticky" style={{ position: "sticky", top: 20 }}>
              <Section title="Live Invoice Preview" icon="📄">
                <div style={{ minHeight: 420 }}>
                  <p style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8, fontWeight: 600 }}>LIVE PREVIEW · Updates as you edit</p>
                  <div style={{ maxHeight: 520, overflowY: "auto" }}>
                    <InvoiceLivePreview data={data} />
                  </div>
                  {uploadedPdfUrl && (
                    <a
                      href={uploadedPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 11, color: "#0ea5e9", fontWeight: 600, textDecoration: "none" }}
                    >
                      📎 View uploaded file {fileName ? `(${fileName})` : ""}
                    </a>
                  )}
                  {uploadedS3Url && (
                    <a
                      href={uploadedS3Url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, marginLeft: 12, fontSize: 11, color: "#15803d", fontWeight: 600, textDecoration: "none" }}
                    >
                      Preview invoice
                    </a>
                  )}
                </div>
              </Section>
            </div>
          </div>
        </div>
      )}

      {/* UPLOADED INVOICES LIST & TABLE SECTION */}
      <div style={{ background: "white", borderRadius: 18, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column", minHeight: isEditorOpen ? 450 : "calc(100vh - 200px)" }}>
        {/* Table Header & Controls */}
        <div style={{ padding: "16px 22px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>Uploaded Invoices History</h2>
            <span style={{ fontSize: 11, background: "#ecfdf5", color: "#059669", fontWeight: 700, padding: "2px 10px", borderRadius: 20, border: "1px solid #a7f3d0" }}>
              {filteredList.length} Invoices
            </span>
          </div>

          {/* Search & Filter Tabs */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* Filter Tabs */}
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
              {(["ALL", "MATCHED", "PENDING"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  style={{
                    border: "none",
                    background: activeTab === tab ? "white" : "transparent",
                    color: activeTab === tab ? "#0f172a" : "#64748b",
                    fontWeight: activeTab === tab ? 700 : 500,
                    fontSize: 11,
                    padding: "5px 12px",
                    borderRadius: 7,
                    cursor: "pointer",
                    boxShadow: activeTab === tab ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  {tab === "ALL" ? "All" : tab === "MATCHED" ? "Matched" : "Pending"}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div style={{ position: "relative" }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input
                type="text"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search invoice no, vendor..."
                style={{
                  paddingLeft: 30,
                  paddingRight: 12,
                  paddingTop: 6,
                  paddingBottom: 6,
                  fontSize: 12,
                  borderRadius: 10,
                  border: "1px solid #cbd5e1",
                  outline: "none",
                  width: 210,
                  background: "#f8fafc",
                }}
              />
            </div>
          </div>
        </div>

        {/* Custom Scrollbar Styles for Laptops & Small Screens */}
        <style>{`
          .vendor-table-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #94a3b8 #f1f5f9;
          }
          .vendor-table-scrollbar::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          .vendor-table-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 6px;
          }
          .vendor-table-scrollbar::-webkit-scrollbar-thumb {
            background-color: #94a3b8;
            border-radius: 6px;
            border: 2px solid #f1f5f9;
          }
          .vendor-table-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: #64748b;
          }
        `}</style>

        {/* Table Body */}
        <div
          className="vendor-table-scrollbar"
          style={{
            overflowX: "auto",
            overflowY: "auto",
            flex: 1,
            maxHeight: "clamp(320px, 58vh, 650px)",
            scrollbarWidth: "thin",
            scrollbarColor: "#94a3b8 #f1f5f9",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          {loadingList ? (
            <div style={{ padding: "48px 20px", textAlign: "center", color: "#64748b" }}>
              <div style={{ width: 26, height: 26, border: "3px solid #cbd5e1", borderTopColor: "#10b981", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
              <span style={{ fontSize: 13, fontWeight: 500 }}>Loading uploaded invoices...</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div style={{ padding: "50px 20px", textAlign: "center" }}>
              <div style={{ width: 50, height: 50, borderRadius: "50%", background: "#f1f5f9", color: "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                <FileText size={24} />
              </div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#334155" }}>No Uploaded Invoices Found</h3>
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, maxWidth: 360, margin: "4px auto 16px" }}>
                {listSearch ? "No invoices match your search criteria." : "You haven't uploaded any invoices yet. Click 'Upload Invoice' to get started."}
              </p>
              <button
                type="button"
                onClick={() => {
                  const input = document.getElementById("pdf-upload-top");
                  if (input) input.click();
                }}
                style={{
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  padding: "8px 18px",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Upload First Invoice
              </button>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <th style={{ padding: "12px 18px" }}>Invoice No</th>
                  <th style={{ padding: "12px 18px" }}>Vendor Name</th>
                  <th style={{ padding: "12px 18px" }}>Bill To</th>
                  <th style={{ padding: "12px 18px" }}>Invoice Date</th>
                  <th style={{ padding: "12px 18px", textAlign: "right" }}>Total Amount</th>
                  <th style={{ padding: "12px 18px", textAlign: "center" }}>Status</th>
                  <th style={{ padding: "12px 18px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody >
                {paginatedInvoices.map((inv: any, idx: number) => {
                  const rawTotal = inv.totals?.taxableValue || inv.taxableValue || inv.totals?.invoiceTotal || inv.totalAmount || "0";
                  const totalStr = formatCurrency(rawTotal);
                  const isCurEditing = dbRecordId && (dbRecordId === inv._id || data.invoiceNo === inv.invoiceNo);

                  return (
                    <tr
                      key={inv._id || idx}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isCurEditing ? "#f0fdf4" : "white",
                        transition: "background 0.15s ease",
                      }}
                    >
                      {/* Invoice No */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 700, fontFamily: "'DM Mono', monospace", color: "#0f172a", fontSize: 13 }}>
                          {inv.invoiceNo || "—"}
                        </div>
                        {inv.fileName && (
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                            📎 {inv.fileName}
                          </div>
                        )}
                      </td>

                      {/* Vendor */}
                      <td style={{ padding: "14px 18px" }}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{inv.vendor?.name || "—"}</div>
                        {inv.vendor?.gstin && (
                          <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "'DM Mono', monospace" }}>
                            GST: {inv.vendor.gstin}
                          </div>
                        )}
                      </td>

                      {/* Bill To */}
                      <td style={{ padding: "14px 18px", color: "#475569" }}>
                        <div style={{ fontWeight: 500 }}>{inv.billTo?.name || "—"}</div>
                      </td>

                      {/* Invoice Date */}
                      <td style={{ padding: "14px 18px", color: "#475569", fontFamily: "'DM Mono', monospace", fontSize: 11 }}>
                        <div>{inv.dated || "—"}</div>
                        {inv.dueDate && (
                          <div style={{ fontSize: 10, color: "#d97706" }}>Due: {inv.dueDate}</div>
                        )}
                      </td>

                      {/* Total Amount (Without Tax / Taxable) */}
                      <td style={{ padding: "14px 18px", textAlign: "right" }}>
                        <span style={{ fontWeight: 800, fontFamily: "'DM Mono', monospace", color: "#059669", fontSize: 14 }}>
                          {totalStr}
                        </span>
                        {inv.totals?.invoiceTotal && inv.totals?.taxableValue && inv.totals.invoiceTotal !== inv.totals.taxableValue && (
                          <div style={{ fontSize: 10, color: "#94a3b8", fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                            Incl. Tax: ₹{inv.totals.invoiceTotal}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: "14px 18px", textAlign: "center" }}>
                        {(() => {
                          const st = String(inv.status || "PENDING_BY_OPS").toUpperCase();

                          // Both ACCEPTED_BY_FINOPS and ACCEPTED_BY_OPS display "Accepted by ops" for the vendor
                          if (st === "ACCEPTED_BY_FINOPS" || st === "ACCEPTED_BY_OPS" || st === "APPROVED") {
                            return (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#ecfdf5", color: "#059669", border: "1px solid #a7f3d0", padding: "3px 9px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                                <CheckCircle2 size={11} /> Accepted by ops
                              </span>
                            );
                          }

                          // Both REJECTED_BY_OPS and REJECTED_BY_FINOPS display "Rejected by ops" for the vendor
                          if (st === "REJECTED_BY_OPS" || st === "REJECTED_BY_FINOPS" || st === "REJECTED") {
                            return (
                              <span
                                style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", padding: "3px 9px", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                                title={inv.rejectionReason ? `Rejected by Ops: ${inv.rejectionReason} (Click Review in Actions to view details)` : "Rejected by Ops"}
                              >
                                <XCircle size={11} /> Rejected by ops
                              </span>
                            );
                          }

                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a", padding: "3px 9px", borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                              <Clock size={11} /> Pending by ops
                            </span>
                          );
                        })()}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "14px 18px", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          {/* Edit / Review Button */}
                          <button
                            type="button"
                            onClick={() => handleSelectInvoiceToEdit(inv)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "#f0fdf4",
                              border: "1px solid #bbf7d0",
                              color: "#16a34a",
                              padding: "5px 10px",
                              borderRadius: 8,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                            title="Edit and review extraction details"
                          >
                            <Edit3 size={12} />
                            <span>Review</span>
                          </button>

                          {/* View PDF Button */}
                          {inv.fileUrl ? (
                            <a
                              href={inv.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                color: "#2563eb",
                                borderRadius: 8,
                                textDecoration: "none",
                              }}
                              title="View Invoice PDF"
                            >
                              <ExternalLink size={13} />
                            </a>
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                color: "#cbd5e1",
                                borderRadius: 8,
                                cursor: "not-allowed",
                              }}
                              title="PDF not available"
                            >
                              <ExternalLink size={13} />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Table Footer / Pagination Controls for Laptops & Mobile */}
        <div
          style={{
            marginTop: "auto",
            padding: "12px 18px",
            background: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 16,
            fontSize: 12,
            color: "#475569",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>
              Showing <strong style={{ color: "#0f172a", fontWeight: 700 }}>{paginatedInvoices.length}</strong> of{" "}
              <strong style={{ color: "#0f172a", fontWeight: 700 }}>{filteredList.length}</strong> invoices
            </span>
            {totalListPages > 1 && (
              <span style={{ color: "#94a3b8" }}>
                • Page <strong style={{ color: "#0f172a", fontWeight: 700 }}>{listPage}</strong> of{" "}
                <strong style={{ color: "#0f172a", fontWeight: 700 }}>{totalListPages}</strong>
              </span>
            )}
          </div>

          {totalListPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                onClick={() => setListPage((p) => Math.max(p - 1, 1))}
                disabled={listPage <= 1}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "6px 12px",
                  background: "white",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  color: "#334155",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: listPage <= 1 ? "not-allowed" : "pointer",
                  opacity: listPage <= 1 ? 0.4 : 1,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <ChevronLeft size={14} />
                <span>Previous</span>
              </button>
              <button
                type="button"
                onClick={() => setListPage((p) => Math.min(p + 1, totalListPages))}
                disabled={listPage >= totalListPages}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "6px 12px",
                  background: "white",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  color: "#334155",
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: listPage >= totalListPages ? "not-allowed" : "pointer",
                  opacity: listPage >= totalListPages ? 0.4 : 1,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <span>Next</span>
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Rejection Reason Detail Popup Modal */}
      {/* IN-APP DUPLICATE INVOICE MODAL POPUP */}
      {duplicateModal && duplicateModal.isOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: 20,
          }}
          onClick={() => setDuplicateModal(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: 16,
              maxWidth: 460,
              width: "100%",
              padding: 24,
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid #fee2e2",
              animation: "fadeIn 0.2s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#dc2626",
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={24} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", margin: 0 }}>
                    Duplicate Invoice Detected
                  </h3>
                  <button
                    type="button"
                    onClick={() => setDuplicateModal(null)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#94a3b8",
                      cursor: "pointer",
                      padding: 4,
                      borderRadius: 6,
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                  Vendor upload notice
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 12,
                padding: "14px 16px",
                marginBottom: 20,
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: "#991b1b",
                  lineHeight: 1.5,
                  margin: 0,
                  fontWeight: 600,
                }}
              >
                {duplicateModal.message}
              </p>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => setDuplicateModal(null)}
                style={{
                  background: "#dc2626",
                  border: "none",
                  color: "white",
                  padding: "9px 24px",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {viewReasonModal?.isOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15, 23, 42, 0.6)",
            padding: 16,
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setViewReasonModal(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: 16,
              maxWidth: 480,
              width: "100%",
              padding: 24,
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              border: "1px solid #fecaca",
              animation: "fadeIn 0.2s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: "#991b1b" }}>Invoice Rejection Reason</h3>
                  <div style={{ fontSize: 11, color: "#64748b", fontFamily: "'DM Mono', monospace" }}>
                    Invoice #{viewReasonModal.invoiceNo}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewReasonModal(null)}
                style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", padding: 4, borderRadius: 6 }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 16, marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                Reason given by Operations:
              </div>
              <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.5, wordBreak: "break-word", maxHeight: 220, overflowY: "auto", whiteSpace: "pre-wrap", fontWeight: 500 }}>
                {viewReasonModal.reason}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => setViewReasonModal(null)}
                style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#475569", padding: "8px 16px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const inv = viewReasonModal.invoice;
                  setViewReasonModal(null);
                  if (inv) handleSelectInvoiceToEdit(inv);
                }}
                style={{ background: "#dc2626", border: "none", color: "white", padding: "8px 18px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
              >
                <Edit3 size={13} /> Edit / Re-submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
